import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'

function Activity() {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, collections, reviews

  useEffect(() => {
    fetchActivities()
  }, [filter])

  const fetchActivities = async () => {
    setLoading(true)
    
    try {
      let allActivities = []

      // Fetch recent collection additions
      if (filter === 'all' || filter === 'collections') {
        const { data: collectionData, error: collectionError } = await supabase
          .from('user_lure_inventory')
          .select(`
            inventory_id,
            user_id,
            status_own,
            status_for_trade,
            status_want_in_trade,
            status_want_to_buy,
            status_wishlist,
            status_pre_ordered,
            status_previously_owned,
            quantity,
            created_at,
            lure_variants (
              variant_id,
              variant_name,
              image_url,
              color_name,
              base_lures (
                base_lure_id,
                name,
                brands (brand_name)
              )
            )
          `)
          .order('created_at', { ascending: false })
          .limit(50)

        if (collectionError) {
          console.error('Error fetching collections:', collectionError)
        } else if (collectionData) {
          // Fetch user data separately for these items
          const userIds = [...new Set(collectionData.map(item => item.user_id))]
          
          const { data: usersData } = await supabase
            .from('public_user_profiles')
            .select('id, username, avatar_url')
            .in('id', userIds)

          // Create a user lookup map
          const userMap = {}
          usersData?.forEach(user => {
            userMap[user.id] = user
          })

          const collectionActivities = collectionData
            .filter(item => userMap[item.user_id]) // Only include items from public profiles
            .map(item => ({
              id: `collection-${item.inventory_id}`,
              type: 'collection',
              user_id: item.user_id,
              username: userMap[item.user_id]?.username,
              avatar_url: userMap[item.user_id]?.avatar_url,
              action: `added to ${[
                item.status_own && 'Own',
                item.status_for_trade && 'For Trade',
                item.status_want_in_trade && 'Want in Trade',
                item.status_want_to_buy && 'Want to Buy',
                item.status_wishlist && 'Wishlist',
                item.status_pre_ordered && 'Pre-Ordered',
                item.status_previously_owned && 'Previously Owned',
              ].filter(Boolean).join(', ') || 'collection'}`,
              lure_name: item.lure_variants?.base_lures?.name,
              lure_id: item.lure_variants?.base_lures?.base_lure_id,
              color_name: item.lure_variants?.variant_name || item.lure_variants?.color_name,
              brand_name: item.lure_variants?.base_lures?.brands?.brand_name,
              image_url: item.lure_variants?.image_url,
              quantity: item.quantity,
              status: [
                item.status_own && 'Own',
                item.status_for_trade && 'For Trade',
                item.status_want_in_trade && 'Want in Trade',
                item.status_want_to_buy && 'Want to Buy',
                item.status_wishlist && 'Wishlist',
                item.status_pre_ordered && 'Pre-Ordered',
                item.status_previously_owned && 'Previously Owned',
              ].filter(Boolean).join(', '),
              created_at: item.created_at
            }))

          allActivities = [...allActivities, ...collectionActivities]
        }
      }

      // Fetch recent reviews
      if (filter === 'all' || filter === 'reviews') {
        const { data: reviewData, error: reviewError } = await supabase
          .from('lure_reviews')
          .select(`
            review_id,
            user_id,
            rating,
            title,
            created_at,
            base_lures (
              base_lure_id,
              name,
              image_url,
              brands (brand_name)
            )
          `)
          .order('created_at', { ascending: false })
          .limit(50)

        if (reviewError) {
          console.error('Error fetching reviews:', reviewError)
        } else if (reviewData) {
          // Fetch user data separately for these reviews
          const userIds = [...new Set(reviewData.map(review => review.user_id))]
          
          const { data: usersData } = await supabase
            .from('public_user_profiles')
            .select('id, username, avatar_url')
            .in('id', userIds)

          // Create a user lookup map
          const userMap = {}
          usersData?.forEach(user => {
            userMap[user.id] = user
          })

          const reviewActivities = reviewData
            .filter(review => userMap[review.user_id]) // Only include reviews from public profiles
            .map(review => ({
              id: `review-${review.review_id}`,
              type: 'review',
              user_id: review.user_id,
              username: userMap[review.user_id]?.username,
              avatar_url: userMap[review.user_id]?.avatar_url,
              action: 'reviewed',
              lure_name: review.base_lures?.name,
              lure_id: review.base_lures?.base_lure_id,
              brand_name: review.base_lures?.brands?.brand_name,
              image_url: review.base_lures?.image_url,
              rating: review.rating,
              review_title: review.title,
              created_at: review.created_at
            }))

          allActivities = [...allActivities, ...reviewActivities]
        }
      }

      // Sort all activities by created_at
      allActivities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

      // Take only the most recent 100
      setActivities(allActivities.slice(0, 100))
      setLoading(false)
    } catch (error) {
      console.error('Error fetching activities:', error)
      setLoading(false)
    }
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const then = new Date(timestamp)
    const seconds = Math.floor((now - then) / 1000)
    
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
    return then.toLocaleDateString()
  }

  const getStatusColor = (status) => {
    const colors = {
      'Own': 'status-own',
      'For Trade': 'status-trade',
      'Want in Trade': 'status-want',
      'Wishlist': 'status-wishlist',
      'Want to Buy': 'status-buy'
    }
    return colors[status] || 'status-default'
  }

  if (loading) {
    return <div className="loading">Loading activity feed...</div>
  }

  return (
    <div className="activity-page">
      <div className="page-header">
        <h1>Community Activity</h1>
        <p className="page-subtitle">
          See what fellow anglers are collecting, reviewing, and trading
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="activity-filters">
        <button
          onClick={() => setFilter('all')}
          className={`activity-filter-btn ${filter === 'all' ? 'active' : ''}`}
        >
          All Activity
        </button>
        <button
          onClick={() => setFilter('collections')}
          className={`activity-filter-btn ${filter === 'collections' ? 'active' : ''}`}
        >
          Collections
        </button>
        <button
          onClick={() => setFilter('reviews')}
          className={`activity-filter-btn ${filter === 'reviews' ? 'active' : ''}`}
        >
          Reviews
        </button>
      </div>

      {/* Activity Feed */}
      {activities.length === 0 ? (
        <div className="no-activities">
          <p>No recent activity found.</p>
          <p className="activity-hint">Activity will appear here as community members add lures to their collections or post reviews.</p>
          <Link to="/browse" className="btn-primary">Browse Lures</Link>
        </div>
      ) : (
        <div className="activity-feed">
          {activities.map(activity => (
            <div key={activity.id} className="activity-item">
              {/* User Avatar */}
              <Link to={`/profile/${activity.user_id}`} className="activity-avatar">
                {activity.avatar_url ? (
                  <img src={activity.avatar_url} alt={activity.username} />
                ) : (
                  <div className="activity-avatar-placeholder">
                    {activity.username?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
              </Link>

              {/* Activity Content */}
              <div className="activity-content">
                <div className="activity-header">
                  <Link to={`/profile/${activity.user_id}`} className="activity-username">
                    {activity.username || 'Anonymous'}
                  </Link>
                  <span className="activity-action">{activity.action}</span>
                  {activity.type === 'collection' && activity.status && (
                    <span className={`activity-status-badge ${getStatusColor(activity.status)}`}>
                      {activity.status}
                    </span>
                  )}
                  {activity.type === 'review' && activity.rating && (
                    <span className="activity-rating-badge">
                      {activity.rating}/10 ⭐
                    </span>
                  )}
                </div>

                {/* Lure Info */}
                <div className="activity-lure-info">
                  {activity.image_url && (
                    <img 
                      src={activity.image_url} 
                      alt={activity.lure_name}
                      className="activity-lure-image"
                    />
                  )}
                  <div className="activity-lure-details">
                    <Link to={`/lure/${activity.lure_id}`} className="activity-lure-name">
                      {activity.lure_name}
                    </Link>
                    {activity.brand_name && (
                      <span className="activity-brand">by {activity.brand_name}</span>
                    )}
                    {activity.color_name && (
                      <span className="activity-color">Color: {activity.color_name}</span>
                    )}
                    {activity.quantity > 1 && (
                      <span className="activity-quantity">Qty: {activity.quantity}</span>
                    )}
                    {activity.review_title && (
                      <p className="activity-review-title">"{activity.review_title}"</p>
                    )}
                  </div>
                </div>

                {/* Timestamp */}
                <div className="activity-timestamp">
                  {getTimeAgo(activity.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More (Future Enhancement) */}
      {activities.length >= 100 && (
        <div className="activity-load-more">
          <p className="activity-note">Showing most recent 100 activities</p>
        </div>
      )}
    </div>
  )
}

export default Activity